/**
 * Import a WhatsApp chat export onto a contact's timeline (R8–R12).
 *
 * Three steps. Upload sends the file to the preview endpoint, which saves
 * nothing. Review asks which sender is the realtor, confirms the day/month
 * order when the file could not settle it, and picks or creates the contact,
 * optionally as an active deal. Import sends the same file again with those
 * choices, and Done reports what was added.
 *
 * Opened from a conversation, the import is preset to that contact.
 *
 * While the import is saving, the dialog cannot be closed and Back is
 * disabled: either would drop the result the save is about to report.
 */

import { Search } from "lucide-react";
import {
	type ChangeEvent,
	type ReactElement,
	type ReactNode,
	useEffect,
	useId,
	useState,
} from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import {
	ChoiceChips,
	FormField,
	isAcceptablePhone,
} from "@/features/realtor/components/enquiries/enquiry-shared";
import { dayKey } from "@/features/realtor/components/messages/message-format";
import {
	useCommitChatImport,
	useContacts,
	usePreviewChatImport,
} from "@/features/realtor/hooks/use-realtor-crm";
import {
	LEAD_TEMPERATURES,
	LEAD_TYPES,
	type LeadTemperature,
	type LeadType,
} from "@/features/realtor/services/leads-api.service";
import {
	type ChatImportInput,
	type ChatImportPreview,
	type ChatImportResult,
	CONTACT_TYPES,
	type ContactType,
	type ImportDateOrder,
} from "@/features/realtor/services/realtor-crm-api.service";
import { StudioButton } from "@/features/studio/theme";
import { cn } from "@/lib/utils";
import { ImportInstructions } from "./import-instructions";

export const CHAT_EXPORT_ACCEPT =
	".txt,.zip,text/plain,application/zip,application/x-zip-compressed";

const SEARCH_DELAY_MS = 250;

type Step = "upload" | "review" | "done";
const STEPS: readonly Step[] = ["upload", "review", "done"];

interface PresetContact {
	id: string;
	name: string;
}

interface ImportChatDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	/** The conversation the dialog was opened from, if any. */
	contact?: PresetContact;
}

export function ImportChatDialog({
	open,
	onOpenChange,
	contact,
}: ImportChatDialogProps): ReactElement {
	const { t } = useTranslation();
	const [committing, setCommitting] = useState(false);
	return (
		<Dialog
			open={open}
			onOpenChange={(next) => {
				if (!next && committing) return;
				onOpenChange(next);
			}}
		>
			<DialogContent className="flex h-[100dvh] max-h-[100dvh] w-full max-w-full flex-col gap-0 overflow-hidden rounded-none border-dashboard-border bg-dashboard-bg p-0 sm:h-auto sm:max-h-[90vh] sm:max-w-lg sm:rounded-xl">
				{/* DialogContent unmounts when closed, so each opening starts at step 1. */}
				<ImportFlow
					contact={contact}
					onClose={() => onOpenChange(false)}
					onCommittingChange={setCommitting}
					title={
						<DialogHeader className="space-y-1 text-left">
							<DialogTitle className="font-display text-2xl font-medium italic text-dashboard-text">
								{contact
									? t("realtor.import.titleFor", { name: contact.name })
									: t("realtor.import.title")}
							</DialogTitle>
							<DialogDescription className="text-dashboard-muted">
								{t("realtor.import.description")}
							</DialogDescription>
						</DialogHeader>
					}
				/>
			</DialogContent>
		</Dialog>
	);
}

// Dates ----------------------------------------------------------------------

interface CalendarDay {
	year: number;
	month: number;
	day: number;
}

/**
 * The India calendar date of `iso`, read with `order` when the preview read
 * it with `parsedWith`. Reading it the other way swaps day and month, which
 * is only offered when every date in the file allows it (`defaulted`); a
 * swap that would not be a date keeps the date as parsed.
 */
function calendarDay(
	iso: string,
	order: ImportDateOrder,
	parsedWith: ImportDateOrder,
): CalendarDay {
	const [year, month, day] = dayKey(iso).split("-").map(Number);
	if (order === parsedWith || day > 12) return { year, month, day };
	return { year, month: day, day: month };
}

function formatDay({ year, month, day }: CalendarDay, locale: string): string {
	return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString(locale, {
		timeZone: "UTC",
		day: "numeric",
		month: "long",
		year: "numeric",
	});
}

// File name ------------------------------------------------------------------

// Android names the export "WhatsApp Chat with Asha.txt", iOS "WhatsApp Chat -
// Asha.zip". A browser adds " (1)" to a repeated download.
const EXPORT_FILE_NAME =
	/^WhatsApp Chat (?:with|-) (.+?)(?: \(\d+\))?\.(?:txt|zip)$/i;

export function contactNameFromFileName(fileName: string): string | null {
	const match = EXPORT_FILE_NAME.exec(fileName.trim());
	return match ? match[1].trim() : null;
}

// Flow -----------------------------------------------------------------------

type ContactMode = "suggested" | "search" | "new";

interface NewContactFields {
	name: string;
	phone: string;
	type: ContactType;
}

interface Deal {
	active: boolean;
	type: LeadType;
	temperature: LeadTemperature;
}

interface FieldErrors {
	name?: string;
	phone?: string;
}

function ImportFlow({
	contact,
	onClose,
	onCommittingChange,
	title,
}: {
	contact?: PresetContact;
	onClose: () => void;
	/** Whether the import is saving; the dialog holds closing until it is not. */
	onCommittingChange: (committing: boolean) => void;
	title: ReactNode;
}): ReactElement {
	const { t } = useTranslation();
	const previewImport = usePreviewChatImport();
	const commitImport = useCommitChatImport();

	useEffect(() => {
		onCommittingChange(commitImport.isPending);
	}, [commitImport.isPending, onCommittingChange]);
	// Unmounted with the dialog: the next opening starts unblocked.
	useEffect(() => () => onCommittingChange(false), [onCommittingChange]);

	const [step, setStep] = useState<Step>("upload");
	const [file, setFile] = useState<File | null>(null);
	const [preview, setPreview] = useState<ChatImportPreview | null>(null);
	const [result, setResult] = useState<ChatImportResult | null>(null);

	const [selfAuthor, setSelfAuthor] = useState<string | null>(null);
	const [dateOrder, setDateOrder] = useState<ImportDateOrder | null>(null);
	const [contactMode, setContactMode] = useState<ContactMode>("new");
	const [picked, setPicked] = useState<PresetContact | null>(null);
	const [newContact, setNewContact] = useState<NewContactFields>({
		name: "",
		phone: "",
		type: "buyer",
	});
	const [deal, setDeal] = useState<Deal>({
		active: false,
		type: "buyer",
		temperature: "warm",
	});
	const [errors, setErrors] = useState<FieldErrors>({});

	const suggested: PresetContact | null =
		contact ?? preview?.matchingContact ?? null;

	const choose = (event: ChangeEvent<HTMLInputElement>): void => {
		const chosen = event.target.files?.[0];
		// Cleared so choosing the same file again (after a fix) fires again.
		event.target.value = "";
		if (!chosen) return;
		previewImport.mutate(
			{ file: chosen, contactId: contact?.id },
			{
				onSuccess: (read) => {
					setFile(chosen);
					setPreview(read);
					setSelfAuthor(null);
					setDateOrder(
						read.dateOrder.confidence === "defaulted"
							? read.previousDateOrder
							: read.dateOrder.order,
					);
					setContactMode(contact || read.matchingContact ? "suggested" : "new");
					setPicked(null);
					setNewContact({
						name:
							contactNameFromFileName(chosen.name) ??
							read.phoneSuggestion?.author ??
							"",
						phone: read.phoneSuggestion?.phone ?? "",
						type: "buyer",
					});
					setDeal({ active: false, type: "buyer", temperature: "warm" });
					setErrors({});
					commitImport.reset();
					setStep("review");
				},
			},
		);
	};

	const back = (): void => {
		previewImport.reset();
		commitImport.reset();
		setStep("upload");
	};

	const contactId =
		contactMode === "suggested"
			? suggested?.id
			: contactMode === "search"
				? picked?.id
				: undefined;
	const canImport =
		Boolean(selfAuthor) &&
		Boolean(dateOrder) &&
		(contactMode === "new" || Boolean(contactId)) &&
		!commitImport.isPending;

	const submit = (): void => {
		if (!file || !preview || !selfAuthor || !dateOrder) return;
		let input: ChatImportInput = { selfAuthor, dateOrder };
		if (contactMode === "new") {
			const name = newContact.name.trim();
			const phone = newContact.phone.trim();
			const nextErrors: FieldErrors = {};
			if (!name) nextErrors.name = t("realtor.import.errors.name");
			if (phone && !isAcceptablePhone(phone)) {
				nextErrors.phone = t("realtor.import.errors.phoneInvalid");
			} else if (deal.active && !phone) {
				nextErrors.phone = t("realtor.import.errors.phoneForDeal");
			}
			setErrors(nextErrors);
			if (Object.keys(nextErrors).length > 0) return;
			const suggestion = preview.phoneSuggestion;
			input = {
				...input,
				name,
				// With a deal the contact takes the deal's type.
				...(deal.active
					? { lead: { type: deal.type, temperature: deal.temperature } }
					: { type: newContact.type }),
				...(phone ? { phone } : {}),
				...(phone && suggestion && phone === suggestion.phone
					? { phoneCountryCode: suggestion.phoneCountryCode }
					: {}),
			};
		} else if (contactId) {
			input = { ...input, contactId };
		}
		commitImport.mutate(
			{ file, input },
			{
				onSuccess: (saved) => {
					setResult(saved);
					setStep("done");
				},
			},
		);
	};

	return (
		<>
			<div className="space-y-3 border-b border-dashboard-border p-4 pr-12 sm:p-6 sm:pr-12">
				{title}
				<StepIndicator current={step} />
			</div>

			<div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-6">
				{step === "upload" && (
					<UploadStep
						onChoose={choose}
						reading={previewImport.isPending}
						error={previewImport.error?.message ?? null}
					/>
				)}
				{step === "review" && preview && (
					<ReviewStep
						preview={preview}
						presetContact={contact ?? null}
						suggested={suggested}
						selfAuthor={selfAuthor}
						onSelfAuthor={setSelfAuthor}
						dateOrder={dateOrder}
						onDateOrder={setDateOrder}
						contactMode={contactMode}
						onContactMode={setContactMode}
						picked={picked}
						onPick={setPicked}
						newContact={newContact}
						onNewContact={(fields) => {
							setNewContact((prev) => ({ ...prev, ...fields }));
							setErrors((prev) => ({
								...prev,
								...("name" in fields ? { name: undefined } : {}),
								...("phone" in fields ? { phone: undefined } : {}),
							}));
						}}
						deal={deal}
						onDeal={(next) => {
							setDeal((prev) => ({ ...prev, ...next }));
							if (next.active === false) {
								setErrors((prev) => ({ ...prev, phone: undefined }));
							}
						}}
						errors={errors}
						commitError={commitImport.error?.message ?? null}
					/>
				)}
				{step === "done" && result && (
					<DoneStep result={result} onClose={onClose} />
				)}
			</div>

			{step === "review" && (
				<div className="flex items-center justify-end gap-2 border-t border-dashboard-border bg-dashboard-bg p-4 sm:px-6">
					<StudioButton
						variant="secondary"
						size="sm"
						onClick={back}
						disabled={commitImport.isPending}
					>
						{t("realtor.import.back")}
					</StudioButton>
					<StudioButton size="sm" onClick={submit} disabled={!canImport}>
						{commitImport.isPending
							? t("realtor.import.importing")
							: t("realtor.import.submit")}
					</StudioButton>
				</div>
			)}
		</>
	);
}

function StepIndicator({ current }: { current: Step }): ReactElement {
	const { t } = useTranslation();
	return (
		<ol
			aria-label={t("realtor.import.stepsLabel")}
			className="flex flex-wrap items-center gap-x-2 text-xs text-dashboard-muted"
		>
			{STEPS.map((step, index) => (
				<li
					key={step}
					aria-current={step === current ? "step" : undefined}
					className={cn(
						"inline-flex items-center gap-2",
						step === current && "font-semibold text-dashboard-text",
					)}
				>
					{index > 0 && <span aria-hidden="true">·</span>}
					{index + 1} {t(`realtor.import.steps.${step}`)}
				</li>
			))}
		</ol>
	);
}

// Step 1 ---------------------------------------------------------------------

function UploadStep({
	onChoose,
	reading,
	error,
}: {
	onChoose: (event: ChangeEvent<HTMLInputElement>) => void;
	reading: boolean;
	error: string | null;
}): ReactElement {
	const { t } = useTranslation();
	const inputId = useId();
	return (
		<div className="space-y-4">
			<ImportInstructions />
			<div className="space-y-2">
				<label
					htmlFor={inputId}
					className={cn(
						"flex min-h-11 cursor-pointer items-center justify-center rounded-lg border border-dashed border-dashboard-border bg-dashboard-paper px-4 text-sm font-medium text-dashboard-text transition-colors hover:bg-dashboard-surface has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-dashboard-accent/40",
						reading && "pointer-events-none opacity-60",
					)}
				>
					{reading
						? t("realtor.import.reading")
						: t("realtor.import.chooseFile")}
					<input
						id={inputId}
						type="file"
						accept={CHAT_EXPORT_ACCEPT}
						onChange={onChoose}
						disabled={reading}
						className="sr-only"
					/>
				</label>
				<p className="text-xs text-dashboard-muted">
					{t("realtor.import.fileHint")}
				</p>
				{error && (
					<p className="text-sm text-dashboard-pill-risk" role="alert">
						{error}
					</p>
				)}
			</div>
		</div>
	);
}

// Step 2 ---------------------------------------------------------------------

interface ReviewStepProps {
	preview: ChatImportPreview;
	presetContact: PresetContact | null;
	suggested: PresetContact | null;
	selfAuthor: string | null;
	onSelfAuthor: (author: string) => void;
	dateOrder: ImportDateOrder | null;
	onDateOrder: (order: ImportDateOrder) => void;
	contactMode: ContactMode;
	onContactMode: (mode: ContactMode) => void;
	picked: PresetContact | null;
	onPick: (contact: PresetContact | null) => void;
	newContact: NewContactFields;
	onNewContact: (fields: Partial<NewContactFields>) => void;
	deal: Deal;
	onDeal: (deal: Partial<Deal>) => void;
	errors: FieldErrors;
	commitError: string | null;
}

function ReviewStep(props: ReviewStepProps): ReactElement {
	const { t, i18n } = useTranslation();
	const { preview, dateOrder } = props;
	const parsedWith = preview.dateOrder.order;
	const shownOrder = dateOrder ?? parsedWith;
	const range = (order: ImportDateOrder): { first: string; last: string } => ({
		first: formatDay(
			calendarDay(preview.dateRange.first, order, parsedWith),
			i18n.language,
		),
		last: formatDay(
			calendarDay(preview.dateRange.last, order, parsedWith),
			i18n.language,
		),
	});
	const shown = range(shownOrder);
	const dayFirst = range("day-first");
	const monthFirst = range("month-first");

	return (
		<div className="space-y-5">
			<div className="space-y-1">
				<p className="text-sm font-medium text-dashboard-text">
					{t("realtor.import.summary", {
						count: preview.messageCount,
						first: shown.first,
						last: shown.last,
					})}
				</p>
				{preview.alreadyImportedCount !== null &&
					preview.alreadyImportedCount > 0 && (
						<p className="text-xs text-dashboard-muted">
							{t("realtor.import.alreadyImported", {
								count: preview.alreadyImportedCount,
							})}
						</p>
					)}
				{preview.warningLines.length > 0 && (
					<p className="text-xs text-dashboard-muted">
						{t("realtor.import.warningLines", {
							count: preview.warningLines.length,
						})}
					</p>
				)}
			</div>

			<RadioGroup legend={t("realtor.import.whichIsYou")}>
				{preview.authors.map((author) => (
					<RadioRow
						key={author.name}
						name="import-self"
						checked={props.selfAuthor === author.name}
						onChange={() => props.onSelfAuthor(author.name)}
						label={author.name}
						detail={t("realtor.import.authorMessages", {
							count: author.messageCount,
						})}
					/>
				))}
			</RadioGroup>

			{preview.dateOrder.confidence === "defaulted" && (
				<RadioGroup
					legend={t("realtor.import.dateOrderQuestion", {
						dayFirst: dayFirst.first,
						monthFirst: monthFirst.first,
					})}
				>
					<RadioRow
						name="import-date-order"
						checked={dateOrder === "day-first"}
						onChange={() => props.onDateOrder("day-first")}
						label={t("realtor.import.dateRange", dayFirst)}
					/>
					<RadioRow
						name="import-date-order"
						checked={dateOrder === "month-first"}
						onChange={() => props.onDateOrder("month-first")}
						label={t("realtor.import.dateRange", monthFirst)}
					/>
				</RadioGroup>
			)}

			<ContactChoice {...props} />

			{props.commitError && (
				<p className="text-sm text-dashboard-pill-risk" role="alert">
					{props.commitError}
				</p>
			)}
		</div>
	);
}

function ContactChoice({
	presetContact,
	suggested,
	contactMode,
	onContactMode,
	picked,
	onPick,
	newContact,
	onNewContact,
	deal,
	onDeal,
	errors,
}: ReviewStepProps): ReactElement {
	const { t } = useTranslation();
	return (
		<div className="space-y-3">
			<RadioGroup legend={t("realtor.import.contactLegend")}>
				{suggested && (
					<RadioRow
						name="import-contact"
						checked={contactMode === "suggested"}
						onChange={() => onContactMode("suggested")}
						label={suggested.name}
						detail={
							presetContact
								? t("realtor.import.contactThisConversation")
								: t("realtor.import.contactMatched")
						}
					/>
				)}
				<RadioRow
					name="import-contact"
					checked={contactMode === "search"}
					onChange={() => onContactMode("search")}
					label={t("realtor.import.contactSearch")}
				/>
				<RadioRow
					name="import-contact"
					checked={contactMode === "new"}
					onChange={() => onContactMode("new")}
					label={t("realtor.import.contactNew")}
				/>
			</RadioGroup>

			{contactMode === "search" &&
				(picked ? (
					<div className="flex items-center justify-between gap-3 rounded-lg border border-dashboard-border bg-dashboard-paper px-3 py-2">
						<span className="min-w-0 truncate text-sm font-medium text-dashboard-text">
							{picked.name}
						</span>
						<StudioButton
							variant="quiet"
							size="xs"
							onClick={() => onPick(null)}
						>
							{t("realtor.import.changeContact")}
						</StudioButton>
					</div>
				) : (
					<ContactSearch onPick={onPick} />
				))}

			{contactMode === "new" && (
				<div className="space-y-3 rounded-lg border border-dashboard-border p-3">
					<FormField
						id="import-contact-name"
						label={t("realtor.import.nameLabel")}
						error={errors.name}
					>
						<Input
							id="import-contact-name"
							value={newContact.name}
							onChange={(event) => onNewContact({ name: event.target.value })}
							maxLength={200}
							aria-invalid={errors.name ? true : undefined}
						/>
					</FormField>
					<FormField
						id="import-contact-phone"
						label={
							deal.active
								? t("realtor.import.phoneLabel")
								: t("realtor.import.phoneOptionalLabel")
						}
						error={errors.phone}
					>
						<Input
							id="import-contact-phone"
							type="tel"
							inputMode="tel"
							value={newContact.phone}
							onChange={(event) => onNewContact({ phone: event.target.value })}
							placeholder="98765 43210"
							maxLength={20}
							aria-invalid={errors.phone ? true : undefined}
						/>
					</FormField>
					{!deal.active && (
						<div className="space-y-1.5">
							<label
								htmlFor="import-contact-type"
								className="block text-sm font-medium text-dashboard-text"
							>
								{t("realtor.import.typeLabel")}
							</label>
							<Select
								value={newContact.type}
								onValueChange={(value) =>
									onNewContact({ type: value as ContactType })
								}
							>
								<SelectTrigger
									id="import-contact-type"
									className="border-dashboard-border bg-dashboard-paper text-dashboard-text"
								>
									<SelectValue />
								</SelectTrigger>
								<SelectContent className="border-dashboard-border bg-dashboard-bg">
									{CONTACT_TYPES.map((type) => (
										<SelectItem key={type} value={type}>
											{t(`realtor.messages.contactTypes.${type}`)}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
					)}

					<label className="flex min-h-11 cursor-pointer items-center gap-2 text-sm font-medium text-dashboard-text sm:min-h-0">
						<input
							type="checkbox"
							checked={deal.active}
							onChange={(event) => onDeal({ active: event.target.checked })}
							className="h-4 w-4 accent-[var(--color-dashboard-accent)]"
						/>
						{t("realtor.import.activeDeal")}
					</label>
					{deal.active && (
						<div className="space-y-3 pl-6">
							<ChoiceChips
								name="import-deal-type"
								legend={t("realtor.import.dealTypeLabel")}
								value={deal.type}
								onChange={(type) => onDeal({ type })}
								options={LEAD_TYPES.map((type) => ({
									value: type,
									label: t(`realtor.leads.types.${type}`),
								}))}
							/>
							<ChoiceChips
								name="import-deal-temperature"
								legend={t("realtor.import.temperatureLabel")}
								value={deal.temperature}
								onChange={(temperature) => onDeal({ temperature })}
								options={LEAD_TEMPERATURES.map((temperature) => ({
									value: temperature,
									label: t(`realtor.leads.temperatures.${temperature}`),
								}))}
							/>
						</div>
					)}
				</div>
			)}
		</div>
	);
}

function ContactSearch({
	onPick,
}: {
	onPick: (contact: PresetContact) => void;
}): ReactElement {
	const { t } = useTranslation();
	const inputId = useId();
	const [query, setQuery] = useState("");
	const [search, setSearch] = useState("");

	useEffect(() => {
		const timer = setTimeout(() => setSearch(query.trim()), SEARCH_DELAY_MS);
		return () => clearTimeout(timer);
	}, [query]);

	const contacts = useContacts({ q: search || undefined });
	const rows = contacts.data?.contacts ?? [];

	return (
		<div className="space-y-2">
			<div className="relative">
				<label htmlFor={inputId} className="sr-only">
					{t("realtor.import.searchLabel")}
				</label>
				<Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-dashboard-muted" />
				<input
					id={inputId}
					type="search"
					value={query}
					onChange={(event) => setQuery(event.target.value)}
					placeholder={t("realtor.import.searchPlaceholder")}
					className="h-10 w-full rounded-lg border border-dashboard-border bg-dashboard-bg pl-9 pr-3 text-sm text-dashboard-text placeholder:text-dashboard-muted focus:outline-none focus:ring-2 focus:ring-dashboard-accent/40"
				/>
			</div>
			{contacts.isPending && (
				<p className="text-xs text-dashboard-muted">
					{t("realtor.import.searching")}
				</p>
			)}
			{contacts.error && (
				<p className="text-xs text-dashboard-pill-risk" role="alert">
					{contacts.error.message}
				</p>
			)}
			{contacts.data && !contacts.isPlaceholderData && rows.length === 0 && (
				<p className="text-xs text-dashboard-muted">
					{search
						? t("realtor.import.noContactMatches", { q: search })
						: t("realtor.import.noContacts")}
				</p>
			)}
			{rows.length > 0 && (
				<ul className="max-h-48 space-y-1 overflow-y-auto">
					{rows.map(({ contact }) => (
						<li key={contact.id}>
							<button
								type="button"
								onClick={() => onPick({ id: contact.id, name: contact.name })}
								className="flex min-h-11 w-full items-center justify-between gap-3 rounded-lg px-2 py-2 text-left text-sm text-dashboard-text transition-colors hover:bg-dashboard-surface sm:min-h-0"
							>
								<span className="min-w-0 truncate font-medium">
									{contact.name}
								</span>
								<span className="shrink-0 text-[11px] text-dashboard-muted">
									{t(`realtor.messages.contactTypes.${contact.type}`)}
								</span>
							</button>
						</li>
					))}
				</ul>
			)}
		</div>
	);
}

function RadioGroup({
	legend,
	children,
}: {
	legend: string;
	children: ReactNode;
}): ReactElement {
	return (
		<fieldset className="space-y-1.5">
			<legend className="mb-1.5 text-sm font-medium text-dashboard-text">
				{legend}
			</legend>
			<div className="space-y-1">{children}</div>
		</fieldset>
	);
}

function RadioRow({
	name,
	checked,
	onChange,
	label,
	detail,
}: {
	name: string;
	checked: boolean;
	onChange: () => void;
	label: string;
	detail?: string;
}): ReactElement {
	return (
		<label
			className={cn(
				"flex min-h-11 cursor-pointer items-center gap-3 rounded-lg border px-3 py-2 text-sm transition-colors sm:min-h-0",
				checked
					? "border-dashboard-accent bg-dashboard-paper"
					: "border-dashboard-border hover:bg-dashboard-surface",
			)}
		>
			<input
				type="radio"
				name={name}
				checked={checked}
				onChange={onChange}
				className="h-4 w-4 shrink-0 accent-[var(--color-dashboard-accent)]"
			/>
			<span className="min-w-0 flex-1 truncate font-medium text-dashboard-text">
				{label}
			</span>
			{detail && (
				<span className="shrink-0 text-[11px] text-dashboard-muted">
					{detail}
				</span>
			)}
		</label>
	);
}

// Step 3 ---------------------------------------------------------------------

function DoneStep({
	result,
	onClose,
}: {
	result: ChatImportResult;
	onClose: () => void;
}): ReactElement {
	const { t } = useTranslation();
	return (
		<div className="space-y-4">
			<p className="text-sm font-medium text-dashboard-text" role="status">
				{t("realtor.import.doneSummary", {
					count: result.imported,
					skipped: result.skipped,
				})}
			</p>
			<div className="flex flex-wrap items-center justify-end gap-2">
				<StudioButton variant="secondary" size="sm" onClick={onClose}>
					{t("realtor.import.close")}
				</StudioButton>
				<Link
					to={`/realtor/messages?contact=${encodeURIComponent(result.contactId)}`}
					onClick={onClose}
					className="inline-flex h-9 items-center rounded-[8px] bg-dashboard-accent px-3.5 text-[13px] font-medium text-dashboard-on-fill hover:opacity-90"
				>
					{t("realtor.import.openConversation")}
				</Link>
			</div>
		</div>
	);
}
